// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Global-admin-only raw dataset output viewer.
 *
 * Surfaces the exact stored dataset payload (report.json) for every result recorded
 * in analysis status — including when the findings badge reads zero, which is
 * otherwise hidden from the UI. Lets an admin tell "the LLM returned nothing"
 * apart from "results were stored but counted/shaped wrong" without digging
 * through S3.
 */

import { useEffect, useState, type FC } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui'
import type { NodeApiClient } from '@/app-shell/services'
import { fetchDatasetByKey, extractLlmMetrics, type DatasetResponse } from '@/applications/shared'
import type { AnalyzedDatasetRef } from '../utils/track'

interface RawOutputDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agentLabel: string
  /** Agent owning these datasets — their keys are only unique within it. */
  agentId: string
  datasetRefs: AnalyzedDatasetRef[]
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  groupId: string
}

interface RawItem {
  documentId: string
  docLabel: string
  datasetKey: string
  version: number
  data: DatasetResponse | null
  error: string | null
}

/** Findings count from the payload itself (vs the recorded findings_count). */
function payloadFindingsCount(data: DatasetResponse | null): number | null {
  const findings = (data?.payload as { findings?: unknown })?.findings
  return Array.isArray(findings) ? findings.length : null
}

/** Tokens billed for the analysis, from the payload's execution metrics. */
function payloadTotalTokens(data: DatasetResponse | null): number | null {
  const metrics = extractLlmMetrics(data)
  return metrics ? metrics.promptTokens + metrics.completionTokens : null
}

export const RawOutputDialog: FC<RawOutputDialogProps> = ({
  open,
  onOpenChange,
  agentLabel,
  agentId,
  datasetRefs,
  apiClient,
  orgId,
  wsId,
  groupId,
}) => {
  const [items, setItems] = useState<RawItem[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setItems(null)
      return
    }
    let cancelled = false
    setLoading(true)
    void (async () => {
      const results = await Promise.all(
        datasetRefs.map(async (ref): Promise<RawItem> => {
          const base = {
            documentId: ref.documentId,
            docLabel: ref.documentName || ref.documentId,
            datasetKey: ref.datasetKey,
            version: ref.version,
          }
          try {
            const data = await fetchDatasetByKey(
              apiClient, orgId, wsId, ref.documentId, agentId, ref.datasetKey, ref.version, groupId,
            )
            return { ...base, data, error: null }
          } catch (err) {
            return { ...base, data: null, error: err instanceof Error ? err.message : 'Not found' }
          }
        }),
      )
      if (!cancelled) {
        setItems(results)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, datasetRefs, agentId, apiClient, orgId, wsId, groupId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="srm-raw-output-dialog">
        <DialogHeader>
          <DialogTitle>Raw output — {agentLabel}</DialogTitle>
          <DialogDescription>
            The exact stored dataset payload for each document, regardless of the findings count
            shown on the track.
          </DialogDescription>
        </DialogHeader>

        {loading || !items ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No stored dataset outputs for this track.</p>
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              const count = payloadFindingsCount(item.data)
              const modelVariant = extractLlmMetrics(item.data)?.modelVariant
              const totalTokens = payloadTotalTokens(item.data)
              return (
                <section
                  key={`${item.documentId}:${item.datasetKey}`}
                  className="rounded-md border"
                  data-testid={`srm-raw-${item.datasetKey}`}
                >
                  <div className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-3 py-2 text-sm">
                    <span className="font-medium truncate">{item.docLabel}</span>
                    <span className="font-mono text-xs text-muted-foreground">{item.datasetKey}</span>
                    <span className="text-xs text-muted-foreground">v{item.version}</span>
                    <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                      {count != null && <span>{count} finding{count === 1 ? '' : 's'} in payload</span>}
                      {modelVariant && <span>{modelVariant}</span>}
                      {totalTokens != null && <span>{totalTokens} tok</span>}
                    </div>
                  </div>
                  {item.data ? (
                    <pre className="max-h-96 overflow-auto p-3 text-xs whitespace-pre-wrap break-words">
                      {JSON.stringify(item.data.payload, null, 2)}
                    </pre>
                  ) : (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      Analysis status exists, but its stored payload could not be loaded
                      ({item.error}). Check the job logs and stored dataset object.
                    </p>
                  )}
                </section>
              )
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
